import React, { useState } from 'react';
import {
  IAuditoriaLancamentoCorretivoResultado,
  IItemLancamentoCorretivo,
} from '../interfaces/IAuditoriaLancamentoCorretivo';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaLancamentoCorretivoResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaLancamentoCorretivoDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const lancamentosCorretivos = resultado.lancamentosCorretivos || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Lançamentos Corretivos (Reversão/Ajuste) — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className='auditoria-kpi auditoria-kpi-disabled'>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          <div className='auditoria-kpi-help'>Sem multa aplicável — aviso de governança documental (CPC 23 / IAS 8).</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Lançamentos Analisados / Corretivos</div>
          <div className='auditoria-kpi-value'>
            {resultado.totalLancamentosAnalisados} / <span className='auditoria-contador auditoria-contador-aviso'>{lancamentosCorretivos.length}</span>
          </div>
          <div className='auditoria-kpi-help'>Lançamentos de reversão/ajuste no 1º dia do mês ou 1º dia útil (exclui saldos/encerramento automáticos).</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base contábil e orientação de justificativa para os lançamentos corretivos.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div className='mb-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
          <div><strong>Orientação de Preenchimento da Justificativa:</strong> {resultado.orientacaoJustificativa}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Lançamentos de Reversão/Ajuste/Estorno/Reclassificação/Correção no 1º Dia do Mês ou 1º Dia Útil</h3>
        <p className='text-muted'>
          Lançamentos (Ativo, Passivo e Resultado) cujo histórico cita "Reversão de Lançamento", "Reversão",
          "Reclassificação", "Correção", "Estorno" ou "Ajuste", registrados no 1º dia calendário do mês e/ou no 1º dia
          útil (quando distintos — ex.: dia 1 cai em fim de semana/feriado) — exigem justificativa detalhada na capa
          da conciliação.
        </p>

        {lancamentosCorretivos.length === 0 && (
          <div className='text-muted mt-4'>Nenhum lançamento de reversão/ajuste identificado no 1º dia do mês ou 1º dia útil no período.</div>
        )}

        {lancamentosCorretivos.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Data</th>
                  <th>Nº Lançamento</th>
                  <th>Termo</th>
                  <th>Histórico</th>
                  <th className='text-end'>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {lancamentosCorretivos.map((item: IItemLancamentoCorretivo, idx) => {
                  const chave = `lc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-aviso' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td>{item.numeroLancamento || '—'}</td>
                        <td>{item.termoEncontrado}</td>
                        <td style={{ maxWidth: 280 }}>{item.historico}</td>
                        <td className='text-end'>{formatCurrency(item.valor)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div className='auditoria-alert status-AVISO mb-2'>
                                <i className='bi bi-info-circle-fill auditoria-alert-icon' />
                                <div>{item.mensagemObrigatoria}</div>
                              </div>
                              <div><strong>Lançamento (Histórico):</strong> {item.historico}.</div>
                              <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
                              <div className='mt-2'><strong>Orientação de Preenchimento da Justificativa:</strong> {resultado.orientacaoJustificativa}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AuditoriaLancamentoCorretivoDetalhe;
